/**
 * Track Conditions-Met Summary (canonical)
 *
 * Single source of truth for track readiness/conditions, computed from
 * persisted state and effective config only (no wizard state).
 *
 * Used by: Track header conditions strip, TrackOverview, Create Session gate.
 */

import {
  DEFAULT_GLOBAL_SESSION_CONFIG,
  resolveEffectiveConfig,
  cycleLayerFromCycle,
  trackLayerFromTrack,
  sessionLayerFromPriorSession,
  type EffectiveSessionConfigV1,
} from './effectiveConfigResolver';

// ============================================================================
// Types
// ============================================================================

export interface TrackConditionsSummary {
  /** S1: Participation locked / configured */
  participationConfigured: boolean;
  /** S2: Item pool locked / configured */
  itemPoolConfigured: boolean;
  /** S3: Criteria locked / configured */
  criteriaConfigured: boolean;
  /** S4: Value ranges/scales accepted for criteria */
  valueScalesConfigured: boolean;
  /** S5: Values sufficient for ranking (converged) */
  valuesSufficient: boolean;
  /** When default session mode is live, a facilitator must be designated */
  liveFacilitatorAvailableWhenNeeded: boolean;
  /** All conditions met to run a ranking session (including live facilitator when applicable) */
  rankingSessionRunnable: boolean;
  /** No unresolved canonical relationship blockers for ranking */
  relationshipReviewClear: boolean;

  /** Resolved effective config for new sessions (for UI that needs timing mode / facilitator) */
  effectiveSessionConfig: EffectiveSessionConfigV1;
  /** Human-readable blocker for live facilitator when not met; null when not applicable or met */
  liveFacilitatorBlockerMessage: string | null;
  /** Human-readable blocker for unresolved relationship review when present */
  relationshipBlockerMessage: string | null;
}

export interface TrackConditionsSummaryInput {
  /** Track (for lock fields and metadata) */
  track: {
    participationLockedAt?: Date | null;
    criteriaLockedAt?: Date | null;
    valuesConvergedAt?: Date | null;
    metadata?: Record<string, unknown> | null;
    /** First-class column — takes precedence over metadata.sessionDefaults */
    defaultTimingMode?: string | null;
    /** First-class column — takes precedence over metadata.sessionDefaults */
    defaultFacilitatorId?: string | null;
  };
  /** Cycle (for metadata.sessionDefaults) */
  cycle: {
    metadata?: Record<string, unknown> | null;
    defaultTimingMode?: string | null;
    defaultFacilitatorId?: string | null;
  } | null | undefined;
  /** Last session (index 0) used for session-layer carry-forward */
  lastSession?: {
    modeConfig?: { timingMode?: 'live' | 'async'; entryMode?: string };
    facilitatorId?: string;
  } | null;
  /** From store: pool locked for this track */
  poolLocked: boolean;
  /** Canonical relationship blocker state for this track */
  relationshipBlockerMessage?: string | null;
}

// ============================================================================
// Condition definitions (exact)
// ============================================================================
//
// - participationConfigured: track.participationLockedAt != null (participation locked)
// - itemPoolConfigured: poolLocked (item pool locked for this track)
// - criteriaConfigured: track.criteriaLockedAt != null (criteria locked)
// - valueScalesConfigured: same as criteria for now (criteria lock implies scales accepted per track); can be refined to "all criteria have range_status accepted"
// - valuesSufficient: track.valuesConvergedAt != null (values converged)
// - liveFacilitatorAvailableWhenNeeded: when effectiveSessionConfig.timingMode === 'live', effectiveSessionConfig.defaultFacilitatorId must be set; otherwise true
// - rankingSessionRunnable: participationConfigured && itemPoolConfigured && criteriaConfigured && valueScalesConfigured && valuesSufficient && liveFacilitatorAvailableWhenNeeded
//

const LIVE_FACILITATOR_BLOCKER_MESSAGE =
  'Live session requires a designated facilitator. Set one in Session Defaults on the Overview tab, or switch to Async mode.';

// ============================================================================
// Resolver
// ============================================================================

/**
 * Compute canonical conditions summary from persisted state and effective config.
 * No wizard or local-only state; all inputs are from DB/state.
 */
export function computeTrackConditionsSummary(input: TrackConditionsSummaryInput): TrackConditionsSummary {
  const {
    track,
    cycle,
    lastSession,
    poolLocked,
    relationshipBlockerMessage,
  } = input;

  const participationConfigured = !!track.participationLockedAt;
  const criteriaConfigured = !!track.criteriaLockedAt;
  const valuesSufficient = !!track.valuesConvergedAt;
  const itemPoolConfigured = poolLocked;
  const valueScalesConfigured = criteriaConfigured;

  const cycleLayer = cycleLayerFromCycle(cycle);
  const trackLayer = trackLayerFromTrack(track);
  const sessionLayer = lastSession
    ? sessionLayerFromPriorSession({
        modeConfig: lastSession.modeConfig,
        facilitatorId: lastSession.facilitatorId,
      })
    : null;
  const effectiveSessionConfig = resolveEffectiveConfig(
    DEFAULT_GLOBAL_SESSION_CONFIG,
    cycleLayer ?? undefined,
    trackLayer ?? undefined,
    sessionLayer
  );

  const isLiveDefault = effectiveSessionConfig.timingMode === 'live';
  const hasDesignatedFacilitator = !!effectiveSessionConfig.defaultFacilitatorId;
  const liveFacilitatorAvailableWhenNeeded = !isLiveDefault || hasDesignatedFacilitator;
  const liveFacilitatorBlockerMessage =
    isLiveDefault && !hasDesignatedFacilitator ? LIVE_FACILITATOR_BLOCKER_MESSAGE : null;
  const relationshipReviewClear = !relationshipBlockerMessage;

  const rankingSessionRunnable =
    participationConfigured &&
    itemPoolConfigured &&
    criteriaConfigured &&
    valueScalesConfigured &&
    valuesSufficient &&
    liveFacilitatorAvailableWhenNeeded &&
    relationshipReviewClear;

  return {
    participationConfigured,
    itemPoolConfigured,
    criteriaConfigured,
    valueScalesConfigured,
    valuesSufficient,
    liveFacilitatorAvailableWhenNeeded,
    rankingSessionRunnable,
    relationshipReviewClear,
    effectiveSessionConfig,
    liveFacilitatorBlockerMessage,
    relationshipBlockerMessage: relationshipBlockerMessage ?? null,
  };
}

export { LIVE_FACILITATOR_BLOCKER_MESSAGE };
